/* WHO IS CARRYING THE BOWL BOOK, AND WHERE ARE THE HOLES.
 *
 * Brian: "Adding an assigned rep to a bowl eligible team with a chart at the
 * top that shows volume per rep."
 *
 * ⭐ THE UNIT IS THE TEAM, NOT THE BOWL GAME. A bowl has its own owner question
 * (42 of 44 are unowned and that is an open decision), and conflating the two
 * would answer neither. This module only ever answers: for THIS SCHOOL, whose
 * desk is it on.
 *
 * 🔴 AND THE HARD PART, MEASURED AGAINST PRODUCTION 2026-10-17 BEFORE A LINE OF
 * UI WAS WRITTEN. There are TWO different answers to "whose desk", they are not
 * the same thing, and the API says so itself:
 *
 *   1. THE SCHOOL COMPANY RECORD'S OWN `sr_assigned_rep`. Authoritative. This
 *      is what Brian asked for.
 *   2. CONFERENCE COVERAGE, from the 2026-2027 Assignment Breakdown, hand-
 *      mapped to body strings. What `/api/fbs-bowl-eligibility/triggers`
 *      attaches. Its own `coverageMeta.note` reads, verbatim:
 *
 *        "The rep(s) who cover the conference, from the 2026-2027 assignment
 *         breakdown. NOT the school company record's own sr_assigned_rep,
 *         which is authoritative and may differ."
 *
 * Those two have disagreed badly before — TAPPS 118/118 wrong, UIL 33.2% against
 * 2.5% — which is exactly why this module NEVER silently picks one. If both are
 * present it reports both and flags the disagreement. If only coverage is
 * present it says so, in the label, on the card, every time.
 *
 * 🔴 WHAT THE BOARD PAYLOAD DOES NOT CARRY, SAID PLAINLY. Measured live:
 * `snapshot.teams[]` is 138 records and NOT ONE of them has an owner-shaped
 * key — the union of keys across all 138 is name, conference, division, wins,
 * losses, conferenceRecord, remaining, needs, fcsWins, group, why,
 * transitional, unconditional. Coverage is attached to the five TRIGGERS only.
 * So a team's desk is resolvable for 5 of 138 today, and this module reports
 * `unresolvable` for the rest rather than rendering an empty cell — a blank
 * where a name belongs is the exact defect just fixed on three surfaces, and
 * on this page specifically the name had been rendering in a fifth table
 * column that sat off-screen at 390px.
 *
 * ⚠️ AND IT DOES NOT FORK THE SHEET TO PAPER OVER THAT. The conference→rep map
 * lives in the backend's `assignment/sheet.json`; copying it into src/ would
 * give the frontend a second, immediately-stale copy of a hand-maintained
 * source of truth, which is the one rule this codebase does not bend. The read
 * belongs to whoever owns the engine: attaching `coverage` to every team in the
 * snapshot, the way triggers already do, lights this whole surface up with no
 * change here. Until then the UI tells the truth about what it cannot see.
 *
 * Pure. No React, no fetch.
 */

const S = (v) => String(v == null ? '' : v).trim()

/* =========================================================================
 * "PHIL" ALWAYS MEANS PHIL AND MADISON.
 *
 * Brian: "'Phil' always means Phil AND Madison. Render the pair."
 *
 * ⚠️ A DISPLAY RULE, AND DELIBERATELY NOT A DATA RULE. There is no second
 * assignment record to read: `REPS` in assignmentPlan.js has seven names and
 * Madison is not one of them, and `sr_assigned_rep` is a single display-name
 * string. What the codebase already records is the relationship —
 * lib/journey.js: *"Madison Gill sits on 340 of Philip's orders as an ops
 * overlay, so 'the owner' and 'who is on it' are genuinely different
 * questions and a single name answers only one."*
 *
 * So this pairs them WHEN RENDERING and writes nothing. Manufacturing a
 * Madison assignment on the record would invent provenance; dropping her from
 * the display answers the wrong one of those two questions.
 * ========================================================================= */
/* Second standing pair, Brian 2026-10-26: "Alexis Usery - a new hire is going
 * to be assigned every one of Zach Muller's championships. She is his
 * (Madison)." Mirrors backend assignment/plan.js STANDING_PAIRS { Zach: 'Alexis' }.
 * Zach stays the lead and the bar; Alexis is named on it, never her own bar. */
export const PAIRED_WITH = {
  'Philip Davanzo III': 'Madison Gill',
  'Zach Muller': 'Alexis Usery',
}
export const PAIR_WHY = 'Madison Gill sits on Philip\'s book as an ops overlay — she is on the '
  + 'work, so the pair is who you are actually reaching. The record stores one name; this is a '
  + 'display pairing and nothing is written.'
/** The reason shown for each lead's pair. Philip's keeps the original wording. */
export const PAIR_WHY_BY_LEAD = {
  'Philip Davanzo III': PAIR_WHY,
  'Zach Muller': 'Alexis Usery is Zach Muller\'s standing pair (Brian Kass, 2026-10-26): she is the '
    + 'support seat on every one of Zach\'s championships, so the pair is who you are actually '
    + 'reaching. Zach stays the lead. This is a display pairing and nothing is written.',
}

/** The name(s) to render for one stored rep. Always an array, never a string. */
export function renderRep(name) {
  const n = S(name)
  if (!n) return { names: [], paired: false }
  const mate = PAIRED_WITH[n]
  return mate ? { names: [n, mate], paired: true, why: PAIR_WHY_BY_LEAD[n] || PAIR_WHY } : { names: [n], paired: false }
}

/** "Philip Davanzo III + Madison Gill" — one line, for a card. */
export function repLine(name) {
  return renderRep(name).names.join(' + ')
}

/* =========================================================================
 * THE FIVE HONEST STATES OF "WHOSE DESK IS THIS TEAM ON".
 *
 * Each one is a DIFFERENT problem with a different response, and flattening
 * them is how a board starts lying. `unresolvable` and `none` look identical
 * on screen if you let them — one means nobody owns this school, the other
 * means this screen cannot see who does. The first is a management problem;
 * the second is a missing field.
 * ========================================================================= */
export const DESK_STATES = {
  school: {
    key: 'school', tone: 'ok',
    label: 'from the school record',
    why: 'The school company record\'s own sr_assigned_rep. This is the authoritative assignment.',
  },
  agree: {
    key: 'agree', tone: 'ok',
    label: 'school record, conference coverage agrees',
    why: 'The school record and the 2026-2027 conference breakdown name the same rep, so there is nothing to reconcile.',
  },
  conflict: {
    key: 'conflict', tone: 'warn',
    label: 'two sources disagree',
    why: 'The school record and the conference breakdown name different reps. Both are shown. '
      + 'Stored assignment and derived coverage have disagreed badly before — TAPPS 118/118, UIL 33.2% '
      + 'against 2.5% — so this board will not pick one for you.',
  },
  /* ⭐ A SIXTH STATE, ADDED 2026-10-17, BECAUSE FIVE MADE THE BOARD LIE BY ONE
   * WORD. The backend can now answer at SCHOOL scope — a desk named by a human
   * for one named campus, with the authorising name on the row (Brian Kass,
   * "Notre dame - Phil and Madison", 2026-10-17). Routed through `coverage` it
   * rendered correctly and was then labelled "conference coverage only", which
   * is false twice over: Notre Dame is an FBS Independent, so there is no
   * conference to have covered it, and the answer did not come from the
   * breakdown at all. A label is a provenance claim; a wrong one is worse than
   * no label, because this is the surface that exists to stop silent picks. */
  assigned: {
    key: 'assigned', tone: 'ok',
    label: 'this school\'s own desk',
    why: 'A desk named for this specific school by a named human, not derived from its '
      + 'conference. One-to-one: it says who this campus belongs to. It is still not the school '
      + 'company record\'s sr_assigned_rep, which is authoritative and may differ.',
  },
  /* ⭐ TWO STATES ADDED 2026-10-26 FOR THE SPLIT-CONFERENCE SELECTOR.
   * Brian: "The split conferences in bowl opportunity need to be able to have a
   * selector to select who is going after it out of the ones you surface."
   * `picked` is a desk chosen on this board, by a named assignment manager,
   * with a reason, append-only. `split` is the state the selector closes:
   * several reps cover the conference and nobody has been picked for this
   * school. Both arrive from the backend; neither is inferred here. */
  picked: {
    key: 'picked', tone: 'ok',
    label: 'picked on the bowl board',
    why: 'Chosen on this board by an assignment manager, with a reason, and recorded append-only '
      + '(who picked it and when stay on the row). It is still not the school company record\'s '
      + 'sr_assigned_rep, which is authoritative and may differ.',
  },
  split: {
    key: 'split', tone: 'warn',
    label: 'school team not assigned',
    why: 'Several reps cover this conference and nobody has been picked for this school yet. '
      + 'Coverage is many-to-one; a school-team decision confirms every assigned person.',
  },
  coverage: {
    key: 'coverage', tone: 'info',
    label: 'conference coverage only',
    why: 'Who covers the CONFERENCE in the 2026-2027 breakdown. Coverage is many-to-one: it does not '
      + 'say which of them is assigned this particular school, and the API\'s own note says it is not the '
      + 'school record\'s authoritative sr_assigned_rep.',
  },
  none: {
    key: 'none', tone: 'bad',
    label: 'no desk',
    why: 'Nobody covers this team\'s body and the school record names nobody. This is a real hole, not a missing read.',
  },
  unresolvable: {
    key: 'unresolvable', tone: 'quiet',
    label: 'not readable here',
    why: 'This board carries no assigned-rep field on a team record, so this screen cannot say whose desk it is. '
      + 'That is a gap in what the page is given, NOT a statement that nobody is assigned to the team — and the two '
      + 'must never look the same.',
  },
}

/**
 * Resolve one team's desk.
 *
 * `team`      a snapshot team record
 * `coverage`  the coverage object for this team, if the payload carried one
 *             (triggers do; board teams do not, today)
 *
 * ⚠️ `school` IS READ FROM WHATEVER THE PAYLOAD CALLS IT, and if it is absent
 * that is reported rather than defaulted. The field does not exist on a board
 * team yet; naming the three plausible spellings here means the day it is added
 * this function starts telling the fuller truth without an edit.
 */
export function teamDesk(team = {}, coverage = null) {
  const cov = coverage || team.coverage || null
  const base = teamDeskCore(team, cov)
  /* The selector's inputs ride on every result, so a card can offer the pick
   * without a second read. Empty for an unsplit conference. */
  const candidates = Array.isArray(cov?.candidates) ? cov.candidates : []
  return { ...base, split: cov?.source !== 'school-team' && !!cov?.split && candidates.length > 1, candidates }
}

function teamDeskCore(team = {}, cov = null) {
  const school = S(team.assignedRep || team.sr_assigned_rep || team.owner)
  const covReps = Array.isArray(cov?.reps) ? cov.reps.map((r) => S(r.name || r.short)).filter(Boolean) : []
  const derivable = cov ? cov.derivable !== false : false

  if (derivable && cov?.source === 'school-team' && cov.scope === 'school' && cov.members?.length && cov.confirmedTeam === true) {
    const names = [...new Set(cov.members.map(m => S(m.name)).filter(Boolean))]
    return {key:'team',tone:'ok',label:cov.confirmedShared ? 'confirmed shared school team' : 'confirmed school team',
      why:'A human-authorized bowl coverage team for this school. This does not change its CRM assignment.',
      school:'',reps:names,coverageReps:names,members:cov.members,body:null,
      recordOwner:school,authorizedBy:cov.authorizedBy,authorizedAt:cov.authorizedAt,
      primaryRequired:cov.primaryRequired === true}
  }
  if (cov?.source === 'school-team' && cov.teamReviewRequired) return {
    ...DESK_STATES.split, label:'school team needs review', school:'', reps:(cov.members || []).map(m => m.name),
    coverageReps:[],body:null,why:"A recorded participant is no longer active. Review this school's team before acting.",
  }

  if (school) {
    const sameSet = covReps.length > 0 && covReps.includes(school)
    if (covReps.length === 0) {
      return { ...DESK_STATES.school, school, reps: renderRep(school).names, coverageReps: [], body: cov?.body || null }
    }
    if (sameSet) {
      return {
        ...DESK_STATES.agree, school, reps: renderRep(school).names,
        coverageReps: covReps.flatMap((r) => renderRep(r).names), body: cov?.body || null,
      }
    }
    return {
      ...DESK_STATES.conflict, school, reps: renderRep(school).names,
      coverageReps: covReps.flatMap((r) => renderRep(r).names),
      body: cov?.body || null, coverageWhy: cov?.why || '',
    }
  }

  /* ⚠️ SCHOOL SCOPE IS CHECKED BEFORE CONFERENCE SCOPE AND THE ORDER IS THE
   * POINT. Both arrive in the same `coverage` shape, so a single branch would
   * have labelled a named school desk as conference coverage — see the note on
   * DESK_STATES.assigned. `scope` is read off the payload rather than inferred
   * from `body` being null, because "no body" is also what a MALFORMED row
   * looks like, and those two must not share a code path. */
  /* A PICK MADE ON THIS BOARD, school or conference scope. Checked first among
   * the coverage states because it is the most recent human decision. */
  if (derivable && covReps.length > 0 && cov?.source === 'pick') {
    return {
      ...DESK_STATES.picked, school: '',
      reps: covReps.flatMap((r) => renderRep(r).names),
      coverageReps: covReps.flatMap((r) => renderRep(r).names),
      body: cov?.body || null, pickScope: cov?.scope || null,
      authorizedBy: cov?.authorizedBy || null, authorizedAt: cov?.authorizedAt || null,
      pickReason: cov?.pick?.reason || '', coverageWhy: cov?.why || '',
    }
  }

  /* SEVERAL REPS, NO LEAD, NO PICK: the split the selector closes. */
  if (derivable && covReps.length > 1 && cov?.needsPick) {
    return {
      ...DESK_STATES.split, school: '',
      reps: covReps.flatMap((r) => renderRep(r).names),
      coverageReps: covReps.flatMap((r) => renderRep(r).names),
      body: cov?.body || null, coverageWhy: cov?.why || '',
    }
  }

  if (derivable && covReps.length > 0 && cov?.scope === 'school') {
    return {
      ...DESK_STATES.assigned, school: '',
      reps: covReps.flatMap((r) => renderRep(r).names),
      coverageReps: covReps.flatMap((r) => renderRep(r).names),
      body: null, authorizedBy: cov?.authorizedBy || null, authorizedAt: cov?.authorizedAt || null,
      coverageWhy: cov?.why || '',
    }
  }

  if (derivable && covReps.length > 0) {
    return {
      ...DESK_STATES.coverage, school: '',
      ...(cov?.source === 'sheet' ? {label: 'provisional conference coverage', why: 'The conference breakdown suggests this desk; it is not a confirmed school assignment.'} : {}),
      reps: covReps.flatMap((r) => renderRep(r).names),
      coverageReps: covReps.flatMap((r) => renderRep(r).names),
      body: cov?.body || null, coverageWhy: cov?.why || '',
      /* A Manage Assignments roster can declare the LEAD of a multi-rep body. */
      lead: S(cov?.lead?.name) || '',
    }
  }

  /* A coverage object that says `derivable: false` is a MEASURED hole — the
   * engine looked and there is no row. No coverage object at all is a read this
   * surface was never given. Different answers. */
  if (cov && derivable === false) {
    return { ...DESK_STATES.none, school: '', reps: [], coverageReps: [], body: cov?.body || null, coverageWhy: cov?.why || '' }
  }
  return { ...DESK_STATES.unresolvable, school: '', reps: [], coverageReps: [], body: null }
}

/* =========================================================================
 * VOLUME PER REP — the chart.
 *
 * 🔴 UNASSIGNED IS A BAR. Brian: "If a slice of the market has no owner,
 * that's the most important thing on the chart, not an omission." So `none`
 * and `unresolvable` each get their own bar, and they are SEPARATE bars
 * because they are separate problems.
 *
 * 🔴 NEVER CAPPED AT THE FIRST N OF n. Every rep with a team is returned. A
 * caller that wants fewer rows must say so and is handed `hidden` so it can
 * say "and N more" rather than quietly truncating the market.
 *
 * ⚠️ THE BARS DO NOT SUM TO THE MEMBERSHIP, AND THAT IS ARITHMETIC RATHER THAN
 * A BUG. Conference coverage is many-to-one: the SEC is covered by two reps, so
 * Georgia counts toward both books. A chart that forced the bars to add to 138
 * would have to pick one rep per team, which is precisely the silent pick this
 * module exists to refuse. So TWO reconciliations are returned and the UI shows
 * the one that actually closes: every team lands in exactly one of
 * owned / noDesk / unresolvable, and that sum IS the membership.
 * ========================================================================= */
export function repVolume(teams = [], { coverageFor = null, membership = null } = {}) {
  const byRep = new Map()
  let owned = 0, noDesk = 0, unresolvable = 0, needsPick = 0
  const noDeskTeams = []
  let paired = false

  for (const t of teams) {
    const cov = typeof coverageFor === 'function' ? coverageFor(t) : null
    const desk = teamDesk(t, cov)

    if (desk.key === 'unresolvable') { unresolvable += 1; continue }
    if (desk.key === 'none') {
      noDesk += 1
      if (noDeskTeams.length < 12) noDeskTeams.push(S(t.name))
      continue
    }

    if (desk.key === 'split') needsPick += 1
    else owned += 1
    /* ⚠️ COUNTED ONCE PER REP ON THE TEAM, and the pairing is NOT double-counted:
     * Philip + Madison is one desk, so the bar is Philip's book with her named on
     * it. Giving Madison her own bar would make the chart claim a book she does
     * not independently carry. */
    const primaries = desk.key === 'team' ? desk.reps : desk.key === 'coverage' && desk.lead
      ? [desk.lead]
      : desk.key === 'coverage' || desk.key === 'conflict' || desk.key === 'split'
      ? [...new Set([...(desk.coverageReps || []), ...(desk.reps || [])])]
      : [S(desk.school) || desk.reps[0]]
    for (const name of primaries) {
      const n = S(name)
      if (!n) continue
      /* ⚠️ THE PAIR FOLDS ON A CONFIRMED SCHOOL TEAM TOO (2026-11-01). The
       * `desk.key !== 'team'` exemption gave Alexis Usery her own bar of 10 on
       * the live board: Arizona, Arizona St., Indiana, Ohio St., Illinois,
       * Wisconsin, Michigan St., Sacramento St., LSU and Arkansas are confirmed
       * school teams that list her as support BESIDE Zach Muller (sales), so
       * none of the ten was missing Zach; she was counted twice. A partner
       * folds into the lead whenever the lead is on the same team; she keeps a
       * bar only for a team that does not carry her lead. */
      if (Object.entries(PAIRED_WITH).some(([lead, mate]) => mate === n && primaries.includes(lead))) { paired = true; continue }
      if (!byRep.has(n)) byRep.set(n, { rep: n, display: repLine(n), n: 0, teams: [] })
      const row = byRep.get(n)
      row.n += 1
      if (row.teams.length < 25) row.teams.push(S(t.name))
    }
  }

  const total = teams.length
  const bars = [...byRep.values()].sort((a, b) => b.n - a.n || a.rep.localeCompare(b.rep))
  const max = Math.max(1, ...bars.map((b) => b.n), needsPick, noDesk, unresolvable)

  return {
    bars,
    max,
    /* The two holes, each its own bar, each with its own meaning. */
    noDesk,
    needsPick,
    noDeskTeams,
    unresolvable,
    owned,
    total,
    membership: membership == null ? total : membership,
    /* 🔴 THE RECONCILIATION THAT ACTUALLY CLOSES, shown rather than asserted. */
    reconciles: owned + needsPick + noDesk + unresolvable === total,
    reconcileLine: `${owned} with a desk + ${needsPick} needing a desk pick + ${noDesk} with no desk + ${unresolvable} not readable here = ${total}`,
    /* Why the rep bars are allowed to sum higher than the total. */
    manyToOne: bars.reduce((n, b) => n + b.n, 0) > owned,
    paired,
  }
}

/* The notes the UI prints. Kept here so the page cannot paraphrase them into
 * something softer than what was measured. */
export const CHART_NOTE =
  'Teams per rep across the whole eligible set. Coverage is many-to-one, so a team in a '
  + 'conference two reps share counts toward both their assignments and the bars add to more '
  + 'than the membership — the line underneath is the sum that closes.'

export const CHART_SOURCE_NOTE =
  'Read live from the FBS bowl eligibility board and its trigger list. Nothing here is stored, '
  + 'computed into money, or written back.'

export const UNRESOLVABLE_NOTE =
  'A team record on this board carries no assigned-rep field, so for most teams this screen cannot say '
  + 'whose desk it is. That is a missing read, not an empty desk, and it is counted separately '
  + 'from the teams that genuinely have no rep. Attaching coverage to every team in the snapshot '
  + '— the way the trigger list already does — is what closes it.'

export const CHART_EMPTY_NOTE =
  'The board read fine and there are no eligible teams to attribute yet. This is an answer, not a failure.'

export const CHART_ERROR_NOTE =
  'The board could not be read, so this chart has nothing to count. It is NOT showing that no rep '
  + 'is assigned anything — a failed read and an unassigned market are different things and must '
  + 'never look alike.'

/* =========================================================================
 * THE CONFERENCE ROLLUP AND THE PICK — helpers for the split-conference
 * selector (2026-10-26). The backend decides; these only word it and shape
 * the request, so the page cannot paraphrase a status into something softer.
 * ========================================================================= */
export const ROLLUP_STATES = {
  assigned: { tone: 'ok', label: 'assigned', why: 'Every school in the conference has one accountable desk.' },
  split: { tone: 'warn', label: 'split: needs a pick', why: 'Every school has coverage, but some sit on a conference several reps cover and nobody has been picked for them.' },
  partial: { tone: 'warn', label: 'partly assigned', why: 'Some schools in the conference have no desk at all.' },
  unassigned: { tone: 'bad', label: 'unassigned', why: 'No school in the conference has a desk.' },
}

export function rollupState(status, details = {}) {
  if (Number.isSafeInteger(details.provisionalDesks) && details.provisionalDesks > 0) return { tone: 'warn', label: status === 'assigned' ? 'provisional coverage' : 'coverage partly provisional', why: `${details.provisionalDesks} displayed desks come from conference coverage, not confirmed school assignments.` }
  return ROLLUP_STATES[status] || { tone: 'quiet', label: 'not readable here', why: 'The board did not carry a conference rollup.' }
}

export const PICK_MIN_REASON = 5

/** The POST body for a pick, or `{ error }` before a request is sent. */
export function pickPayload({ school = '', conference = '', rep = '', reason = '', action = 'set' } = {}) {
  const r = S(reason)
  if (action !== 'clear' && !S(rep)) return { error: 'Pick a rep.' }
  if (!S(school) && !S(conference)) return { error: 'Choose a school or conference.' }
  if (r.length < PICK_MIN_REASON) return { error: 'Say why, in a sentence.' }
  return S(school)
    ? { scope: 'school', school: S(school), rep: S(rep), reason: r, action }
    : { scope: 'conference', conference: S(conference), rep: S(rep), reason: r, action }
}

/** "Philip Davanzo III + Madison Gill" for a candidate from the API. */
export function candidateLabel(c = {}) {
  return c.pairedWith ? `${c.name} + ${c.pairedWith}` : S(c.name || c.short)
}

/** A recent ledger row uses short rep IDs; expand only known display aliases. */
export function pickedRepLine(reps = []) {
  const aliases = { Philip: 'Philip Davanzo III', Zach: 'Zach Muller' }
  return (Array.isArray(reps) ? reps : []).map(name => repLine(aliases[name] || name)).join(' + ')
}
